// frontend/src/components/game/containers/MapContainer/MapContainer.jsx
import { useState, useRef, useEffect } from 'react';
import useGameStore from "@store/useGameStore";
import useMapStore from "@store/useMapStore";
import { getTileSize } from "@utils/constants";
import { useBreakpoint } from "@utils/responsive";

// Capas
import TerrainLayer from "./TerrainLayer";
import HighlightLayer from './HighlightLayer';
import ObjectsLayer from "./ObjectsLayer";
import ActionLinesLayer from "./ActionLinesLayer";
import ActionIconsLayer from "./ActionIconsLayer";
import AnimationLayer from "./AnimationLayer";
// ❌ Ya no importamos PlannedActionsLayer

import feedback from "@components/ui/general/feedback";
import "./MapContainer.css";

export default function MapContainer() {
  const game = useGameStore.getState().game;
  const isResolutionPhase = useGameStore.getState().isResolutionPhase;
  const tiles = useMapStore.getState().selectTerrainTiles();

  // Get reactive tile size based on current breakpoint
  const breakpoint = useBreakpoint();
  const tileSize = getTileSize(breakpoint);

  // Log breakpoint changes and tile size (throttled by useBreakpoint)
  // useEffect(() => {
  //   console.info('[MapContainer] Breakpoint changed:', {
  //     breakpoint,
  //     tileSize,
  //     viewportWidth: document.documentElement.clientWidth,
  //     viewportHeight: document.documentElement.clientHeight
  //   });
  // }, [breakpoint, tileSize]);

  // Track drag-to-scroll state
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);
  const dragStartPos = useRef({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 });

  // Add global mouse up listener to handle dragging outside the container
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDragging) {
        setIsDragging(false);
      }
    };

    window.addEventListener('mouseup', handleGlobalMouseUp);
    return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
  }, [isDragging]);

  // Early return check AFTER all hooks
  if (!game?.map || tiles.length === 0) {
    feedback.warningOnce("[MapManager]Map data is not available yet.");
    return null;
  }

  const mapWidth = game.map.width ?? 0;
  const mapHeight = game.map.height ?? 0;

  const mapStyle = {
    width: `${mapWidth * tileSize}px`,
    height: `${mapHeight * tileSize}px`,
    '--tile-size': `${tileSize}px`, // CSS variable for child components
  };

  // Drag-to-scroll handlers - scroll the parent (map-area) element
  const getScrollParent = () => containerRef.current?.parentElement;

  const handleMouseDown = (e) => {
    const scrollParent = getScrollParent();
    if (!scrollParent) return;

    setIsDragging(true);
    dragStartPos.current = {
      x: e.clientX,
      y: e.clientY,
      scrollLeft: scrollParent.scrollLeft,
      scrollTop: scrollParent.scrollTop
    };
  };

  const handleMouseMove = (e) => {
    const scrollParent = getScrollParent();
    if (!isDragging || !scrollParent) return;

    e.preventDefault();

    const deltaX = e.clientX - dragStartPos.current.x;
    const deltaY = e.clientY - dragStartPos.current.y;

    scrollParent.scrollLeft = dragStartPos.current.scrollLeft - deltaX;
    scrollParent.scrollTop = dragStartPos.current.scrollTop - deltaY;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  return (
    <div
      ref={containerRef}
      className="map-container"
      style={{
        ...mapStyle,
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none'
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
    >
        {/* Base layers always in same order */}
        <TerrainLayer tiles={tiles} mapWidth={mapWidth} tileSize={tileSize} />
        <HighlightLayer tileSize={tileSize} />
        <ActionLinesLayer tileSize={tileSize} />

        {/* Conditional layer ordering based on game mode */}
        {isResolutionPhase ? (
          <>
            {/* Cinematic mode: Action icons below units */}
            <ActionIconsLayer tileSize={tileSize} />
            <ObjectsLayer tileSize={tileSize} />
          </>
        ) : (
          <>
            {/* Normal mode: Units below action icons */}
            <ObjectsLayer tileSize={tileSize} />
            <ActionIconsLayer tileSize={tileSize} />
          </>
        )}

        {/* Visual effects always on top */}
        <AnimationLayer />
      </div>
  );
}